import React from 'react';
import PageHero from './PageHero';
import {
  Users, Home, Trophy, UserCheck, Monitor, Laptop,
  Languages, Hash, FlaskConical, Globe, Leaf, Cpu,
  Lightbulb, Target, Clock, Badge, Star, Sparkles,
  Compass, BookOpen, CheckCircle, ArrowRight, Award, Palette, Hammer
} from 'lucide-react';
import primaryImg from '../assets/PRIMARY SCHOOL.png';
import ibPypModel from '../assets/IB_PYP_MODEL.png';
import ibLogo from '../assets/IB CONTINUUM.png';
import cambridgeLogo from '../assets/CAMBRIDGE.png';

// ── Shared sub-components ──────────────────────────────────────────────────────

function SectionTitle({ eyebrow, title, subtitle }) {
  return (
    <div className="text-center max-w-3xl mx-auto mb-16">
      {eyebrow && (
        <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-gold/30 bg-gold/10">
          <Sparkles className="w-3.5 h-3.5 text-gold" />
          <span className="text-gold-ink text-[11px] font-bold tracking-[0.2em] uppercase">{eyebrow}</span>
        </div>
      )}
      <h2 className="text-3xl md:text-5xl font-heading font-bold text-navy leading-tight mb-4 uppercase">{title}</h2>
      {subtitle && <p className="text-ink/75 text-[16px] md:text-[17px] leading-relaxed">{subtitle}</p>}
    </div>
  );
}

function FeatureBox({ icon, title, desc, accent }) {
  const border = {
    orange: 'border-[#f97316]', blue: 'border-[#3b82f6]', green: 'border-[#22c55e]',
    red: 'border-[#ef4444]', pink: 'border-[#ec4899]', purple: 'border-[#a855f7]',
    yellow: 'border-[#eab308]', violet: 'border-[#8b5cf6]',
  };
  const bg = {
    orange: 'bg-[#fff7f0]', blue: 'bg-[#eff6ff]', green: 'bg-[#f0fdf4]',
    red: 'bg-[#fef2f2]', pink: 'bg-[#fdf2f8]', purple: 'bg-[#faf5ff]',
    yellow: 'bg-[#fefce8]', violet: 'bg-[#f5f3ff]',
  };
  const ic = {
    orange: 'text-[#f97316]', blue: 'text-[#3b82f6]', green: 'text-[#22c55e]',
    red: 'text-[#ef4444]', pink: 'text-[#ec4899]', purple: 'text-[#a855f7]',
    yellow: 'text-[#eab308]', violet: 'text-[#8b5cf6]',
  };
  return (
    <div className={`rounded-2xl border-l-4 p-6 h-full shadow-sm hover:shadow-md transition-shadow duration-300 ${border[accent]} ${bg[accent]}`}>
      <div className={`mb-4 ${ic[accent]}`}>{icon}</div>
      <h4 className="text-lg font-heading font-semibold text-navy mb-2">{title}</h4>
      <p className="text-ink/70 text-[14.5px] leading-relaxed">{desc}</p>
    </div>
  );
}

function SubjectFeature({ icon, title, desc, align = 'right' }) {
  const isLeft = align === 'left';
  return (
    <div className={`flex items-start gap-4 mb-10 ${isLeft ? 'flex-row-reverse text-right' : ''}`}>
      <div className="shrink-0 w-12 h-12 rounded-xl bg-navy flex items-center justify-center text-white shadow-md">
        {icon}
      </div>
      <div className={isLeft ? 'text-right' : ''}>
        <h3 className="text-lg font-heading font-semibold text-navy mb-1">{title}</h3>
        <p className="text-ink/70 text-[14.5px] leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function InfoCard({ icon, iconBg, checkColor, title, items }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-line p-8 h-full hover:shadow-md transition-shadow">
      <div className="flex items-center gap-4 mb-6">
        <div className={`w-12 h-12 rounded-full ${iconBg} flex items-center justify-center shrink-0`}>
          {icon}
        </div>
        <h3 className="text-xl font-heading font-semibold text-navy">{title}</h3>
      </div>
      <ul className="space-y-4">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <CheckCircle size={18} className={`shrink-0 mt-0.5 ${checkColor}`} />
            <span className="text-ink/80 text-[14.5px] leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function PrimarySchoolPage({ setPage, t }) {
  const pData = t?.primarySchoolPage || {};
  const intro = pData?.intro || {};
  const exhibition = pData?.exhibition || {};
  const cambridge = pData?.cambridge || {};
  const themes = pData?.themes || [];
  const pillars = pData?.pillars || [];
  const cta = pData?.cta || {};

  const handleNavigate = (pageKey) => {
    if (setPage) setPage(pageKey);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pillarIcons = [
    <Hammer key="hammer" className="w-6 h-6 text-navy" />,
    <Languages key="lang" className="w-6 h-6 text-navy" />,
    <Palette key="art" className="w-6 h-6 text-navy" />,
    <Trophy key="trophy" className="w-6 h-6 text-navy" />
  ];

  const navCategory = t?.nav?.[2]?.label || 'Academics';
  const homeLabel = t?.nav?.[0]?.label || 'Home';

  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen text-ink pb-0">

      {/* ── Page Hero ─────────────────────────────────────────────────────────── */}
      <PageHero
        eyebrow={pData.eyebrow || navCategory}
        title={pData.title || 'Primary School (Grades 1–5)'}
        subtitle={
          pData.subtitle ||
          'IB Primary Years Programme (PYP) with Optional Cambridge IGCSE Checkpoint Pathway.'
        }
        breadcrumbs={pData.breadcrumbs || [homeLabel, navCategory, 'Primary School']}
        bgImage={primaryImg}
      />

      {/* ── Core Section: IB Primary Years Programme (PYP) & Transdisciplinary Framework ── */}
      <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px] py-16 lg:py-24">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 mb-5 px-4 py-1.5 rounded-full border border-gold/30 bg-gold/10">
              <Compass className="w-3.5 h-3.5 text-gold" />
              <span className="text-gold-ink text-[11px] font-bold tracking-[0.2em] uppercase">
                {intro.eyebrow || 'IB PRIMARY YEARS PROGRAMME'}
              </span>
            </div>

            <h2 className="text-3xl md:text-5xl font-heading font-bold text-navy mb-6 leading-tight uppercase">
              {intro.title || 'A Transdisciplinary Framework Placing the Child at the Centre'}
            </h2>

            <div className="space-y-5 text-ink/80 text-[16px] md:text-[17.5px] leading-[1.8]">
              <p className="m-0">
                {intro.p1 ||
                  'The Primary Years Programme provides a transdisciplinary learning framework that places the child at the centre of learning. Students are encouraged to become active inquirers who develop understanding through meaningful experiences and connections across different areas of learning.'}
              </p>
              
              <p className="m-0">
                {intro.p2 ||
                  'The programme explores learning through the six PYP transdisciplinary themes, helping students understand themselves and their place in the world. Along the way, they develop greater agency and ownership of their learning, supported by the IB Learner Profile and the Approaches to Learning.'}
              </p>

              <p className="m-0">
                {intro.p3 ||
                  'Learning at GMIS extends beyond acquiring knowledge. Students are encouraged to reflect on their understanding and turn learning into meaningful action. Their experiences are enriched through STEAM learning in our dedicated Makerspace, together with specialist learning in languages, arts, music, and physical education.'}
              </p>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3 pt-6 border-t border-line/70">
              <span className="px-3.5 py-1.5 rounded-full text-xs font-heading font-bold uppercase tracking-wider bg-gold/15 text-gold-ink border border-gold/30">
                {intro.badge1 || 'IB PYP (Grades 1–5)'}
              </span>
              <span className="px-3.5 py-1.5 rounded-full text-xs font-heading font-bold uppercase tracking-wider bg-navy/10 text-navy border border-navy/20">
                {intro.badge2 || 'Cambridge Checkpoint Optional'}
              </span>
            </div>
          </div>

          {/* Right: The IB PYP Circular Model Diagram */}
          <div className="flex flex-col items-center">
            <div className="relative bg-white p-6 md:p-8 rounded-3xl shadow-xl border border-slate-200/80 max-w-[480px] w-full text-center">
              <img
                src={ibPypModel}
                alt="IB Primary Years Programme Model"
                className="w-full h-auto object-contain mx-auto transition-transform duration-500 hover:scale-[1.02]"
              />
              <div className="mt-5 pt-4 border-t border-line/60">
                <div className="text-xs font-heading font-bold uppercase tracking-wider text-navy">
                  IB Primary Years Programme Curricular Model
                </div>
                <div className="text-xs text-ink/65 mt-1">
                  Transdisciplinary Themes • Learner Profile • Approaches to Learning
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Section: The Six PYP Transdisciplinary Themes ────────────────────── */}
      <div className="bg-white py-16 lg:py-24 border-y border-line/60">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px]">
          <SectionTitle
            eyebrow={t?.utilAdm === 'Penerimaan' ? 'KERANGKA INKUIRI' : 'TRANSDISCIPLINARY FRAMEWORK'}
            title={pData.themesTitle || 'The Six PYP Transdisciplinary Themes'}
            subtitle={
              pData.themesSubtitle ||
              'Global, conceptual themes connecting learning across traditional subject boundaries.'
            }
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {themes.map((theme, idx) => (
              <div
                key={idx}
                className="bg-[#fcfdfd] rounded-2xl p-7 border border-slate-200/80 shadow-[0_4px_16px_rgba(0,0,0,0.03)] hover:shadow-lg hover:border-gold/50 transition-all duration-300 flex flex-col justify-between group"
              >
                <div>
                  <div className="w-10 h-10 rounded-xl bg-navy/5 text-navy flex items-center justify-center font-heading font-bold text-sm mb-4 group-hover:bg-gold group-hover:text-navy-deep transition-colors">
                    0{idx + 1}
                  </div>
                  <h3 className="m-0 font-heading font-bold text-xl text-navy mb-2 group-hover:text-gold-ink transition-colors">
                    {theme.title}
                  </h3>
                  <p className="m-0 text-[14px] text-ink/75 leading-relaxed">
                    {theme.desc}
                  </p>
                </div>
                <div className="pt-4 mt-4 border-t border-line/60 flex items-center text-xs font-semibold text-gold-ink">
                  <CheckCircle className="w-3.5 h-3.5 text-gold mr-1.5 shrink-0" />
                  <span>Central Idea Inquiry</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Highlight Dual Cards: PYP Exhibition & Optional Cambridge Checkpoint ── */}
      <div className="py-16 lg:py-24">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Card 1: PYP Exhibition & International-Mindedness */}
            <div className="bg-white rounded-3xl p-8 lg:p-12 border border-slate-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 mb-4 px-3.5 py-1.5 rounded-full border border-gold/30 bg-gold/10">
                  <Award className="w-4 h-4 text-gold" />
                  <span className="text-gold-ink text-[11px] font-bold tracking-[0.2em] uppercase">
                    {exhibition.eyebrow || 'CULMINATING JOURNEY'}
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-heading font-bold text-navy mb-4">
                  {exhibition.title || 'The PYP Exhibition & International-Mindedness'}
                </h3>
                <p className="text-ink/80 text-[15px] md:text-[16px] leading-[1.75] mb-6">
                  {exhibition.desc ||
                    'The journey culminates in the PYP Exhibition, where students take greater ownership of an inquiry and demonstrate how their learning can connect with the world around them. Throughout the programme, international-mindedness remains an important part of developing young learners who are open to different perspectives and ready to engage with the wider world.'}
                </p>
              </div>

              <div className="pt-6 border-t border-line/70 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img src={ibLogo} alt="IB World School" className="h-8 w-auto object-contain" />
                  <span className="text-xs font-heading font-bold text-navy uppercase tracking-wider">
                    Culminating Capstone Showcase
                  </span>
                </div>
              </div>
            </div>

            {/* Card 2: Optional Cambridge IGCSE Checkpoint Pathway */}
            <div className="bg-white rounded-3xl p-8 lg:p-12 border border-slate-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 mb-4 px-3.5 py-1.5 rounded-full border border-navy/20 bg-navy/5">
                  <BookOpen className="w-4 h-4 text-navy" />
                  <span className="text-navy text-[11px] font-bold tracking-[0.2em] uppercase">
                    {cambridge.badge || 'Cambridge Assessment International'}
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-heading font-bold text-navy mb-4">
                  {cambridge.title || 'Optional Cambridge IGCSE Checkpoint Pathway'}
                </h3>
                <p className="text-ink/80 text-[15px] md:text-[16px] leading-[1.75] mb-6">
                  {cambridge.desc ||
                    'For students who choose the optional Cambridge IGCSE Checkpoint pathway, the programme provides an additional international academic benchmark alongside their PYP learning. This allows students and families to gain a clearer understanding of academic progress while continuing to benefit from the broader PYP experience.'}
                </p>
              </div>

              <div className="pt-6 border-t border-line/70 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img src={cambridgeLogo} alt="Cambridge International School" className="h-8 w-auto object-contain" />
                  <span className="text-xs font-heading font-bold text-navy uppercase tracking-wider">
                    International Academic Benchmark
                  </span>
                </div>
                <button
                  onClick={() => handleNavigate('curriculum-approach')}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-navy hover:text-gold-ink transition-colors cursor-pointer"
                >
                  <span>{cambridge.button || 'Explore Dual Pathway'}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Section: Enriched Learning (Makerspace, Languages, Arts, Health) ── */}
      <div className="bg-white py-16 lg:py-24 border-y border-line/60">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px]">
          <SectionTitle
            eyebrow={t?.utilAdm === 'Penerimaan' ? 'PENGAYAAN PEMBELAJARAN' : 'ENRICHED CURRICULUM'}
            title={pData.pillarsTitle || 'Enriched Learning at GMIS'}
            subtitle={
              pData.pillarsSubtitle ||
              'Bridging conceptual understanding with creative action, STEAM, and specialist excellence.'
            }
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((pillar, idx) => (
              <div
                key={idx}
                className="bg-[#fcfdfd] rounded-2xl p-7 border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
              >
                <div>
                  <div className="w-12 h-12 rounded-xl bg-gold/15 flex items-center justify-center mb-5 text-navy">
                    {pillarIcons[idx] || <Lightbulb className="w-6 h-6 text-navy" />}
                  </div>
                  <h4 className="font-heading font-bold text-lg text-navy mb-2">
                    {pillar.title}
                  </h4>
                  <p className="text-ink/75 text-[14px] leading-relaxed m-0">
                    {pillar.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Facilities & Programs ─────────────────────────────────────────────── */}
      <div className="py-20 lg:py-24">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px]">
          <SectionTitle
            eyebrow={t?.utilAdm === 'Penerimaan' ? 'KAMPUS & FASILITAS' : 'Campus & Resources'}
            title={t?.utilAdm === 'Penerimaan' ? 'Fasilitas & Program Khusus' : 'Facilities & Special Programs'}
            subtitle={
              t?.utilAdm === 'Penerimaan'
                ? 'Lingkungan belajar berstandar internasional yang menunjang eksplorasi dan kecerdasan jamak.'
                : 'Modern learning environments fostering collaboration, research, and scientific discovery.'
            }
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureBox accent="orange" icon={<Users size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Rasio Kelas Rendah' : 'Small Class Sizes'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Perhatian personal dan bimbingan individual untuk mengoptimalkan potensi setiap siswa.' : 'Low student-to-teacher ratio ensuring every child receives individual attention and guidance.'} />
            <FeatureBox accent="blue" icon={<Home size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Ruang Kelas IB Nyaman' : 'Spacious IB Classrooms'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Ruang kelas terang, ber-AC, dan dirancang khusus untuk kerja kolaboratif serta inkuiri.' : 'Bright, well-ventilated, purposefully designed spaces that promote inquiry and collaboration.'} />
            <FeatureBox accent="green" icon={<Award size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Makerspace Inovatif' : 'Makerspace Innovation'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Ruang kreasi teknologi, robotika, dan pembelajaran STEAM dengan peralatan lengkap.' : 'Dedicated hands-on STEAM lab for robotics, coding, prototyping, and design thinking.'} />
            <FeatureBox accent="red" icon={<BookOpen size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Literasi Bahasa (LSRW)' : 'LSRW Literacy Focus'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Penekanan menyeluruh pada Mendengarkan, Berbicara, Membaca, dan Menulis di setiap jenjang.' : 'Consistent emphasis on Listening, Speaking, Reading, and Writing across all year levels.'} />
            <FeatureBox accent="pink" icon={<Trophy size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Kompetisi & Olimpiade' : 'Competitions & Olympiads'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Siswa aktif berpartisipasi dalam kompetisi akademik, sains, dan seni internasional.' : 'Students participate in local and international academic and arts competitions confidently.'} />
            <FeatureBox accent="purple" icon={<UserCheck size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Pembelajaran Terdiferensiasi' : 'Personalised Learning'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Instruksi yang disesuaikan dengan kekuatan, gaya belajar, dan minat setiap anak.' : "Differentiated instruction tailored to every learner's individual strengths and needs."} />
            <FeatureBox accent="yellow" icon={<Monitor size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Papan Tulis Interaktif' : 'Smart Classrooms'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Layar sentuh digital dan sumber daya multimedia terintegrasi dalam kegiatan belajar.' : 'Interactive displays, projectors, and digital resources integrated into every lesson.'} />
            <FeatureBox accent="violet" icon={<Laptop size={28} />}
              title={t?.utilAdm === 'Penerimaan' ? 'Literasi Digital & ICT' : 'Tech-Enabled Learning'}
              desc={t?.utilAdm === 'Penerimaan' ? 'Sesi TIK khusus untuk mempersiapkan siswa menjadi warga digital yang bertanggung jawab.' : 'Dedicated ICT and digital literacy sessions preparing students for a technology-driven world.'} />
          </div>
        </div>
      </div>

      {/* ── Curriculum Subjects ───────────────────────────────────────────────── */}
      <div className="bg-white py-20 lg:py-24 border-y border-line/60">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px]">
          <SectionTitle
            eyebrow={t?.utilAdm === 'Penerimaan' ? 'KERANGKA KURIKULUM' : 'Learning Framework'}
            title={t?.utilAdm === 'Penerimaan' ? 'Mata Pelajaran Kurikulum SD' : 'Primary Curriculum Subjects'}
            subtitle={
              t?.utilAdm === 'Penerimaan'
                ? 'Kerangka Transdisipliner IB PYP & Cambridge Checkpoint — Kelas 1 hingga 5'
                : 'IB PYP Transdisciplinary Framework & Cambridge Checkpoint — Grades 1 to 5'
            }
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">

            {/* Left */}
            <div>
              <SubjectFeature align="left" icon={<Languages size={20} />}
                title={t?.utilAdm === 'Penerimaan' ? 'Sastra Bahasa Inggris' : 'English Language Arts'}
                desc={t?.utilAdm === 'Penerimaan' ? 'Prosa, puisi, tata bahasa, fonik, dan penulisan kreatif melalui pendekatan literasi PYP.' : 'Prose, poetry, grammar, phonics, and creative writing through the IB PYP literacy approach.'} />
              <SubjectFeature align="left" icon={<Globe size={20} />}
                title={t?.utilAdm === 'Penerimaan' ? 'Bahasa Indonesia' : 'Bahasa Indonesia'}
                desc={t?.utilAdm === 'Penerimaan' ? 'Mengembangkan kefasihan membaca, menulis, berbicara, dan apresiasi budaya nasional.' : 'Developing local language fluency in reading, writing, speaking, and cultural appreciation.'} />
              <SubjectFeature align="left" icon={<Hash size={20} />}
                title={t?.utilAdm === 'Penerimaan' ? 'Matematika Konseptual' : 'Mathematics'}
                desc={t?.utilAdm === 'Penerimaan' ? 'Matematika konseptual dan prosedural melalui inkuiri dunia nyata dan pemecahan masalah.' : 'Conceptual and procedural maths through real-world inquiry, problem-solving, and reasoning.'} />
              <SubjectFeature align="left" icon={<Languages size={20} />}
                title={t?.utilAdm === 'Penerimaan' ? 'Bahasa Mandarin / Prancis' : 'Mandarin / French'}
                desc={t?.utilAdm === 'Penerimaan' ? 'Bahasa internasional kedua diajarkan sejak Kelas 1 untuk membangun komunikasi global.' : 'A second world language introduced from Grade 1 to build global communication skills.'} />
            </div>

            {/* Centre Image */}
            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gold/20 rounded-full blur-3xl scale-110" />
                <img
                  src={primaryImg}
                  alt="GMIS Primary School"
                  className="relative z-10 rounded-2xl shadow-xl w-full max-w-[340px] h-[420px] object-cover object-top mx-auto border-4 border-white"
                />
              </div>
            </div>

            {/* Right */}
            <div>
              <SubjectFeature align="right" icon={<FlaskConical size={20} />}
                title={t?.utilAdm === 'Penerimaan' ? 'Sains Praktis' : 'Science'}
                desc={t?.utilAdm === 'Penerimaan' ? 'Eksperimen langsung di laboratorium dan inkuiri terhadap dunia fisik dan hayati.' : 'Hands-on experiments, lab work, and inquiry into the natural and physical world.'} />
              <SubjectFeature align="right" icon={<Globe size={20} />}
                title={t?.utilAdm === 'Penerimaan' ? 'Ilmu Sosial (IPS / SOSE)' : 'Social Studies / SOSE'}
                desc={t?.utilAdm === 'Penerimaan' ? 'Sejarah, geografi, budaya, dan tanggung jawab warga dunia dalam unit terpadu.' : 'History, geography, cultures, and civic responsibility explored through transdisciplinary units.'} />
              <SubjectFeature align="right" icon={<Leaf size={20} />}
                title={t?.utilAdm === 'Penerimaan' ? 'Pendidikan Pribadi & Sosial' : 'Personal & Social Education'}
                desc={t?.utilAdm === 'Penerimaan' ? 'Kesejahteraan mental, kecerdasan emosional, dan kepedulian sosial yang terjalin erat.' : 'Wellbeing, emotional intelligence, and global citizenship woven across all units of inquiry.'} />
              <SubjectFeature align="right" icon={<Cpu size={20} />}
                title={t?.utilAdm === 'Penerimaan' ? 'TIK & Literasi Digital' : 'ICT & Digital Literacy'}
                desc={t?.utilAdm === 'Penerimaan' ? 'Dasar-dasar pemrograman, alat digital, dan pemanfaatan teknologi yang bijak.' : 'Coding basics, digital tools, and responsible technology use introduced progressively.'} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Programme Details Info Cards ─────────────────────────────────────── */}
      {/* <div className="py-20 lg:py-24">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px]">
          <SectionTitle
            eyebrow={t?.utilAdm === 'Penerimaan' ? 'DETAIL PROGRAM' : 'Programme Details'}
            title={t?.utilAdm === 'Penerimaan' ? 'Tinjauan Program Sekolah Dasar' : 'Primary School Programme Overview'}
            subtitle={
              t?.utilAdm === 'Penerimaan'
                ? 'Kelas 1 hingga 5 — Menumbuhkan Generasi Berwawasan Global dan Berbudi Pekerti Luhur'
                : 'Grades 1 to 5 — Nurturing Young Minds for a Brighter Future'
            }
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">

            <InfoCard
              icon={<Lightbulb size={22} className="text-blue-600" />}
              iconBg="bg-blue-50"
              checkColor="text-blue-500"
              title={t?.utilAdm === 'Penerimaan' ? 'Pendekatan & Metode Mengajar' : 'Our Approach & Teaching Methodology'}
              items={
                t?.utilAdm === 'Penerimaan'
                  ? [
                      'Inkuiri transdisipliner IB PYP — siswa menjelajahi konsep dunia nyata lintas batas mata pelajaran.',
                      'Pembelajaran berbasis konsep yang dipimpin siswa untuk mengasah pemikiran kritis dan kreatif.',
                      'Penekanan pada komunikasi, kolaborasi tim, kepemimpinan, dan keterampilan riset mandiri.',
                      'Fasilitas Makerspace canggih terintegrasi dalam kurikulum untuk pengalaman STEAM nyata.',
                      'Instruksi terdiferensiasi untuk mendukung kecerdasan majemuk dan gaya belajar beragam.',
                      'Penilaian formatif dan sumatif yang selaras dengan tolok ukur IB dan Cambridge Checkpoint.'
                    ]
                  : [
                      'IB PYP transdisciplinary inquiry — students explore real-world concepts across subject areas.',
                      'Concept-based, student-led learning that builds critical thinking and creative problem-solving.',
                      'Emphasis on developing communication, collaboration, leadership, and research skills.',
                      'Integrated Makerspace and STEAM innovation lab experiences for hands-on problem solving.',
                      'Differentiated instruction to support diverse learners and multiple intelligences.',
                      'Formative and summative assessments aligned with IB standards and Cambridge Checkpoint.'
                    ]
              }
            />

            // Program Focus
            <InfoCard
              icon={<Target size={22} className="text-green-600" />}
              iconBg="bg-green-50"
              checkColor="text-green-500"
              title={t?.utilAdm === 'Penerimaan' ? 'Fokus Utama Program' : 'Programme Focus'}
              items={
                t?.utilAdm === 'Penerimaan'
                  ? [
                      'Penguatan literasi Bahasa Inggris, penalaran matematika, dan inkuiri ilmiah mendalam.',
                      'Menumbuhkan pelajar berwawasan global melalui tema-tema dunia dan saling menghormati budaya.',
                      'Mendorong kreativitas, kemampuan berpidato/presentasi, dan rasa percaya diri akademis.',
                      'Eksplorasi coding, robotika, dan teknologi informasi sejak kelas awal di Makerspace.',
                      'Pameran PYP (Exhibition) Kelas 5 — proyek puncak kepemimpinan dan inkuiri mandiri siswa.'
                    ]
                  : [
                      'Strengthening English literacy, mathematical reasoning, and scientific inquiry.',
                      'Developing internationally-minded learners through global themes and cross-cultural units.',
                      'Encouraging creativity, public speaking, and academic confidence across all grades.',
                      'Hands-on coding, robotics, and Makerspace exploration integrated from early primary.',
                      'Grade 5 PYP Exhibition — the centrepiece student-led capstone project of the programme.'
                    ]
              }
            />

            // Timings
            <InfoCard
              icon={<Clock size={22} className="text-cyan-600" />}
              iconBg="bg-cyan-50"
              checkColor="text-cyan-500"
              title={t?.utilAdm === 'Penerimaan' ? 'Jam Belajar Sekolah' : 'School Timings'}
              items={
                t?.utilAdm === 'Penerimaan'
                  ? [
                      'Senin hingga Jumat: 07.30 – 14.30 WIB.',
                      'Program pengayaan dan ekstrakurikuler setelah jam sekolah tersedia hingga 16.30 WIB.',
                      'Waktu khusus untuk seni rupa, olahraga, musik, dan membaca terintegrasi dalam jadwal mingguan.',
                      'Sesi bimbingan remedial dan pendalaman materi tersedia untuk siswa yang membutuhkan.'
                    ]
                  : [
                      'Monday to Friday: 7:30 AM – 2:30 PM.',
                      'Optional after-school enrichment and extracurricular clubs available until 4:30 PM.',
                      'Activity periods for arts, sports, music, and reading integrated into the weekly timetable.',
                      'Remedial guidance and extension support sessions available for individual learning needs.'
                    ]
              }
            />

            // Uniform
            <InfoCard
              icon={<Badge size={22} className="text-amber-600" />}
              iconBg="bg-amber-50"
              checkColor="text-amber-500"
              title={t?.utilAdm === 'Penerimaan' ? 'Tata Busana & Seragam' : 'Uniform & Dress Code'}
              items={
                t?.utilAdm === 'Penerimaan'
                  ? [
                      'Seragam resmi GMIS yang rapi, elegan, dan nyaman dikenakan sepanjang hari.',
                      'Pakaian olahraga (PE kit) terpisah digunakan pada jadwal pelajaran pendidikan jasmani.',
                      'Seragam formal lengkap digunakan saat upacara bendera, perayaan sekolah, dan Pameran PYP.',
                      'Siswa diharapkan mengenakan seragam dengan rasa bangga dan senantiasa berpenampilan rapi.'
                    ]
                  : [
                      'Smart, neat, and comfortable GMIS uniform — easy to maintain and wear daily.',
                      'Separate PE/sports kit required for physical education sessions.',
                      'Formal uniform required for assemblies, PYP exhibitions, and school events.',
                      'Students are expected to wear their uniform with pride and maintain a neat appearance.'
                    ]
              }
            />
          </div>

          // Note to Parents
          <div className="bg-[#fef8f0] border border-gold/30 rounded-2xl p-8 shadow-sm mt-8">
            <h3 className="text-lg font-heading font-bold text-navy mb-5 flex items-center gap-3">
              <Star size={18} className="text-gold" />
              <span>{t?.utilAdm === 'Penerimaan' ? 'Catatan Penting untuk Orang Tua' : 'A Note to Parents'}</span>
            </h3>
            <div className="flex flex-col gap-3.5">
              {(t?.utilAdm === 'Penerimaan'
                ? [
                    'Orang tua adalah mitra terpenting dalam pendidikan anak. Komunikasi teratur dengan wali kelas memastikan dukungan belajar terbaik baik di sekolah maupun di rumah.',
                    'Dukung kebiasaan membaca mandiri, rasa ingin tahu ilmiah, dan hobi kreatif di rumah untuk memperkaya pengalaman belajar anak di kelas.',
                    'Manajemen waktu layar (screen time) yang bijak serta pola tidur yang teratur sangat dianjurkan guna menjaga energi dan fokus belajar anak di sekolah.'
                  ]
                : [
                    'Parents are valued partners in education. Regular communication with class teachers ensures the best learning support for your child both at school and at home.',
                    'Encourage independent reading, curiosity, and creative hobbies at home to complement and enrich what your child experiences in the classroom each day.',
                    'Balanced screen time and a consistent home routine — especially a regular bedtime — are strongly recommended to support your child’s energy and focus at school.'
                  ]
              ).map((text, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center shrink-0 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold" />
                  </div>
                  <p className="text-ink/80 text-[14.5px] leading-relaxed m-0">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div> */}

      {/* ── Section: CTA (solid bg-navy with NO gradient) ────────────────── */}
      <div className="w-full bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-16">
          <div className="relative overflow-hidden bg-navy rounded-3xl p-12 md:p-20 text-center shadow-2xl">
            <div className="relative z-10">
              <h3 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight uppercase">
                {cta.title || 'Give Your Child a World-Class Foundation'}
              </h3>
              <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-10">
                {cta.subtitle ||
                  'Schedule a campus visit to experience our IB Primary Years Programme, dedicated Makerspace, and meet our academic leadership.'}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => handleNavigate('schedule-visit')}
                  className="bg-gold text-navy px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white hover:text-navy transition-colors duration-300 cursor-pointer shadow-md"
                >
                  {cta.button || 'Schedule a Visit'}
                </button>
                <button
                  onClick={() => handleNavigate('admissions-process')}
                  className="bg-white/10 text-white px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white/20 transition-colors duration-300 border border-white/20 cursor-pointer"
                >
                  {cta.secondaryButton || 'Explore Admissions'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
